import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { NutritionAvailability, NutritionAvailabilityBlock } from '@/types';
import { cn } from '@/lib/utils';

/** Días en el orden en que se muestran; el valor es `day_of_week` (0 = domingo). */
const WEEK_DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
];

interface NutritionAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availability: NutritionAvailability[];
  blocks: NutritionAvailabilityBlock[];
  isSaving: boolean;
  onSaveAvailability: (row: Partial<NutritionAvailability>) => Promise<void>;
  onDeleteAvailability: (id: string) => Promise<void>;
  onAddBlock: (blockedAt: string, reason: string | null) => Promise<void>;
  onDeleteBlock: (id: string) => Promise<void>;
}

interface DayDraft {
  day_of_week: number;
  is_active: boolean;
  start_time: string;
  end_time: string;
  slot_minutes: number;
}

const toTimeInput = (value?: string | null) => {
  if (!value) return '09:00';
  const [h, m] = value.split(':');
  return `${h.padStart(2, '0')}:${(m || '00').padStart(2, '0')}`;
};

const toTimeValue = (value: string) => (value.length === 5 ? value : '09:00');

/**
 * Configuración de la agenda de reservas:
 * - horario recurrente semanal (por día)
 * - bloqueos puntuales
 */
export function NutritionAvailabilityDialog({
  open,
  onOpenChange,
  availability,
  blocks,
  isSaving,
  onSaveAvailability,
  onDeleteAvailability,
  onAddBlock,
  onDeleteBlock,
}: NutritionAvailabilityDialogProps) {
  const [drafts, setDrafts] = useState<Record<number, DayDraft>>({});
  const [blockAt, setBlockAt] = useState('');
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    if (!open) return;
    const next: Record<number, DayDraft> = {};
    WEEK_DAYS.forEach((day) => {
      const found = availability.find((a) => a.day_of_week === day.value);
      next[day.value] = found
        ? {
            day_of_week: day.value,
            is_active: found.is_active,
            start_time: toTimeInput(found.start_time),
            end_time: toTimeInput(found.end_time),
            slot_minutes: found.slot_minutes ?? 30,
          }
        : {
            day_of_week: day.value,
            is_active: false,
            start_time: '09:00',
            end_time: '13:00',
            slot_minutes: 30,
          };
    });
    setDrafts(next);
    setBlockAt('');
    setBlockReason('');
  }, [open, availability]);

  const updateDraft = (day: number, patch: Partial<DayDraft>) => {
    setDrafts((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  };

  const handleSaveDay = async (day: number) => {
    const draft = drafts[day];
    if (!draft) return;
    if (draft.is_active && toTimeValue(draft.end_time) <= toTimeValue(draft.start_time)) {
      return;
    }
    await onSaveAvailability({
      day_of_week: day,
      is_active: draft.is_active,
      start_time: toTimeValue(draft.start_time),
      end_time: toTimeValue(draft.end_time),
      slot_minutes: Number(draft.slot_minutes) || 30,
    });
  };

  const handleAddBlock = async () => {
    if (!blockAt) return;
    await onAddBlock(new Date(blockAt).toISOString(), blockReason.trim() || null);
    setBlockAt('');
    setBlockReason('');
  };

  const upcomingBlocks = blocks
    .filter((b) => new Date(b.blocked_at).getTime() >= Date.now() - 86400000)
    .sort((a, b) => new Date(a.blocked_at).getTime() - new Date(b.blocked_at).getTime());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Disponibilidad de la agenda</DialogTitle>
          <DialogDescription>
            Define los días y horas en los que la clienta puede reservar. Los horarios se muestran en la
            zona horaria del gimnasio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3">
            {WEEK_DAYS.map((day) => {
              const draft = drafts[day.value];
              if (!draft) return null;
              const saved = availability.find((a) => a.day_of_week === day.value);

              return (
                <div
                  key={day.value}
                  className={cn(
                    'flex flex-wrap items-center gap-3 rounded-lg border p-3',
                    draft.is_active ? 'border-primary/40 bg-primary/5' : 'border-border'
                  )}
                >
                  <Switch
                    checked={draft.is_active}
                    onCheckedChange={(checked) => updateDraft(day.value, { is_active: checked })}
                  />
                  <p className="w-24 text-sm font-medium">{day.label}</p>

                  <div className="flex items-center gap-1">
                    <Input
                      type="time"
                      value={draft.start_time}
                      onChange={(e) => updateDraft(day.value, { start_time: e.target.value })}
                      className="w-28"
                      disabled={!draft.is_active}
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="time"
                      value={draft.end_time}
                      onChange={(e) => updateDraft(day.value, { end_time: e.target.value })}
                      className="w-28"
                      disabled={!draft.is_active}
                    />
                  </div>

                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={5}
                      step={5}
                      value={draft.slot_minutes}
                      onChange={(e) => updateDraft(day.value, { slot_minutes: Number(e.target.value) })}
                      className="w-20"
                      disabled={!draft.is_active}
                    />
                    <span className="text-xs text-muted-foreground">min/cita</span>
                  </div>

                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveDay(day.value)}
                      disabled={isSaving}
                    >
                      Guardar
                    </Button>
                    {saved && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                        onClick={() => onDeleteAvailability(saved.id)}
                        disabled={isSaving}
                        title="Quitar este día"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-3 rounded-lg border border-border p-3">
            <p className="text-sm font-semibold">Bloqueos puntuales</p>
            <p className="text-xs text-muted-foreground">
              Feriados, vacaciones o citas internas: las horas bloqueadas no se pueden reservar.
            </p>

            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Fecha y hora</Label>
                <Input
                  type="datetime-local"
                  value={blockAt}
                  onChange={(e) => setBlockAt(e.target.value)}
                />
              </div>
              <div className="min-w-[160px] flex-1 space-y-1">
                <Label className="text-xs">Motivo</Label>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Ej. feriado"
                />
              </div>
              <Button onClick={handleAddBlock} disabled={!blockAt || isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Agregar
              </Button>
            </div>

            {upcomingBlocks.length > 0 && (
              <ul className="space-y-1">
                {upcomingBlocks.map((block) => (
                  <li
                    key={block.id}
                    className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm"
                  >
                    <span>
                      {new Date(block.blocked_at).toLocaleString('es-ES', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                      {block.reason ? ` · ${block.reason}` : ''}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={() => onDeleteBlock(block.id)}
                      disabled={isSaving}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          {upcomingBlocks.length > 0 && (
            <Badge variant="secondary" className="mr-auto">
              {upcomingBlocks.length} bloqueo(s) activo(s)
            </Badge>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
